// src/features/tasks/pages/TasksListPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

// Components
import TasksPageHeader from '../components/TaskListPage/TasksPageHeader';
import TasksFiltersAndControls from '../components/TaskListPage/TasksFiltersAndControls';
import TasksResultsSummary from '../components/TaskListPage/TasksResultsSummary';
import TasksGrid from '../components/TaskListPage/TasksGrid';
import GlobalTaskActionsDialog from '../components/GlobalTaskActionsDialog';

// Hooks
import { useUnifiedTaskFiltering } from '../hooks/useUnifiedTaskFiltering';
import { useTaskCategories } from '../hooks/useTaskCategories';
import { useTaskActions } from '../hooks/useTaskActions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { useTasks } from '../hooks/useTasks';
import i18n from '../../../i18n';

const VIEW_OPTIONS = [
  { value: 'cards', icon: 'ViewModuleIcon', label: 'Card View' },
  { value: 'list', icon: 'ViewListIcon', label: 'List View' },
];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Tasks' },
  { value: 'my-tasks', label: 'My Tasks' },
  { value: 'today', label: 'Today\'s Tasks' },
  { value: 'overdue', label: 'Overdue Tasks' },
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const RECURRENCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'adhoc', label: 'Ad-hoc' },
  { value: 'recurring', label: 'Recurring' },
];

const SEARCH_DEBOUNCE_MS = 500; // Debounce delay in milliseconds

export default function TasksListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // Debounce timer ref
  const searchDebounceTimer = useRef(null);
  
  // Local search state for immediate UI updates
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // Helper functions for URL parameter validation
  const getValidFilterFromParams = useCallback(() => {
    const urlFilter = searchParams.get('filter');
    return FILTER_OPTIONS.some(opt => opt.value === urlFilter) ? urlFilter : 'all';
  }, [searchParams]);

  const getValidCategoryFromParams = useCallback(() => {
    return searchParams.get('category') || '';
  }, [searchParams]);

  const getSearchTermFromParams = useCallback(() => {
    return searchParams.get('search') || '';
  }, [searchParams]);

  const getViewModeFromParams = useCallback(() => {
    const urlView = searchParams.get('view');
    if (urlView && VIEW_OPTIONS.some(opt => opt.value === urlView)) {
      return urlView;
    }
    return localStorage.getItem('tasks_view_mode') || 'cards';
  }, [searchParams]);

  const getRecurrenceFilterFromParams = useCallback(() => {
    const urlRecurrence = searchParams.get('recurrence');
    return RECURRENCE_OPTIONS.some(opt => opt.value === urlRecurrence) ? urlRecurrence : 'all';
  }, [searchParams]);

  // View mode state (UI-only, not part of server filtering)
  const [viewMode, setViewMode] = useState(() => getViewModeFromParams());

  // Server-side filtering hook
  const {
    filteredTasks,
    currentFilter,
    selectedCategory,
    searchTerm,
    recurrenceFilter,
    hasActiveFilters,
    loading,
    error,
    clearError,
    handleFilterChange,
    handleCategoryChange,
    handleSearchChange,
    handleRecurrenceFilterChange,
    refreshData,
    getCurrentFilterParams,
  } = useUnifiedTaskFiltering(getValidFilterFromParams(), getRecurrenceFilterFromParams());

  // Additional hooks
  const { categories, fetchTaskCategories } = useTaskCategories();
  const { deleteTask } = useTasks();

  // Task actions hook
  const {
    handleStateChange,
    dialogStates,
    dialogHandlers,
  } = useTaskActions({
    refreshTasks: refreshData,
    onSuccess: (data) => {
      console.log('Task action completed successfully:', data);
    }
  });

  // URL parameter management
  const updateUrlParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Initialize local search term from URL
  useEffect(() => {
    const urlSearch = getSearchTermFromParams();
    setLocalSearchTerm(urlSearch);
  }, []);

  // Initialize filters from URL on mount
  useEffect(() => {
    const urlFilter = getValidFilterFromParams();
    const urlCategory = getValidCategoryFromParams();
    const urlSearch = getSearchTermFromParams();
    const urlView = getViewModeFromParams();
    const urlRecurrence = getRecurrenceFilterFromParams();

    // Set filter state if different from current
    if (urlFilter !== currentFilter) handleFilterChange(urlFilter);
    if (urlCategory !== selectedCategory) handleCategoryChange(urlCategory);
    if (urlSearch !== searchTerm) handleSearchChange(urlSearch);
    if (urlView !== viewMode) setViewMode(urlView);
    if (urlRecurrence !== recurrenceFilter) handleRecurrenceFilterChange(urlRecurrence);

    // Always ensure URL reflects current state
    updateUrlParams({
      filter: urlFilter,
      category: urlCategory,
      search: urlSearch,
      view: urlView,
      recurrence: urlRecurrence,
    });
  }, []); // Only run on mount

  // Update URL when filter state changes
  useEffect(() => {
    updateUrlParams({
      filter: currentFilter,
      category: selectedCategory,
      search: searchTerm,
      view: viewMode,
      recurrence: recurrenceFilter,
    });
  }, [currentFilter, selectedCategory, searchTerm, viewMode, recurrenceFilter, updateUrlParams]);

  // Load initial data
  useEffect(() => {
    fetchTaskCategories({ active_only: true });
  }, [fetchTaskCategories]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Event handlers
  const handleRefresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  const handleCreateClick = useCallback(() => {
    navigate('/tasks/create');
  }, [navigate]);

  const handleViewTask = useCallback((task) => {
    navigate(`/tasks/${task.task_id}`);
  }, [navigate]);

  const handleEditTask = useCallback((task) => {
    navigate(`/tasks/${task.task_id}/edit`);
  }, [navigate]);

  const handleDeleteTask = useCallback((task) => {
    showConfirmDialog({
      title: 'Delete Task',
      message: `Are you sure you want to delete the task "${task.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteTask(task.task_id);
          if (result.success) {
            showSuccess(i18n.t('tasks:success.task_deleted'));
            await refreshData(); // Refresh filtered data
          } else {
            showError(result.error || 'Failed to delete task');
          }
        } catch (error) {
          showError(i18n.t('tasks:errors.delete_failed'));
        }
      },
    });
  }, [showConfirmDialog, deleteTask, showSuccess, showError, refreshData]);

  const handleFilterChangeWithUrl = useCallback((newFilter) => {
    handleFilterChange(newFilter);
  }, [handleFilterChange]);

  const handleCategoryChangeWithUrl = useCallback((event) => {
    handleCategoryChange(event);
  }, [handleCategoryChange]);

  // Debounced search handler
  const handleSearchChangeWithUrl = useCallback((event) => {
    const newSearchTerm = event.target.value;
    
    // Update local state immediately for responsive UI
    setLocalSearchTerm(newSearchTerm);
    
    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Set new timer for debounced search
    searchDebounceTimer.current = setTimeout(() => {
      handleSearchChange(event);
    }, SEARCH_DEBOUNCE_MS);
  }, [handleSearchChange]);

  const handleViewModeChange = useCallback((newViewMode) => {
    setViewMode(newViewMode);
    // Save to localStorage
    try {
      localStorage.setItem('tasks_view_mode', newViewMode);
    } catch (error) {
      console.warn('Failed to save view mode to localStorage:', error);
    }
  }, []);

  const handleRecurrenceFilterChangeWithUrl = useCallback((newRecurrence) => {
    handleRecurrenceFilterChange(newRecurrence);
  }, [handleRecurrenceFilterChange]);

  // Permission checks
  const canCreate = permissions.hasScope('task:create');
  const canUpdate = permissions.hasScope('task:update');
  const canDelete = permissions.hasScope('task:delete');
  const canChangeState = permissions.hasScope('task:state:update');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <TasksPageHeader
        onRefresh={handleRefresh}
        onCreateClick={handleCreateClick}
        canCreate={canCreate}
        loading={loading}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Filters and Controls */}
      <TasksFiltersAndControls
        currentFilter={currentFilter}
        selectedCategory={selectedCategory}
        searchTerm={localSearchTerm}
        categories={categories}
        onFilterChange={handleFilterChangeWithUrl}
        onCategoryChange={handleCategoryChangeWithUrl}
        onSearchChange={handleSearchChangeWithUrl}
      />

      {/* Results Summary */}
      <TasksResultsSummary
        filteredTasksCount={filteredTasks.length}
        currentFilter={currentFilter}
        selectedCategory={selectedCategory}
        searchTerm={searchTerm}
        categories={categories}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onFilterChange={handleFilterChange}
        recurrenceFilter={recurrenceFilter}
        onRecurrenceFilterChange={handleRecurrenceFilterChangeWithUrl}
      />

      {/* Tasks Display */}
      <TasksGrid
        tasks={filteredTasks}
        viewMode={viewMode}
        loading={loading}
        currentFilter={currentFilter}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canChangeState={canChangeState}
        onView={handleViewTask}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onStateChange={handleStateChange}
        onCreateClick={handleCreateClick}
      />

      {/* Global Task Actions Dialog */}
      <GlobalTaskActionsDialog
        dialogStates={dialogStates}
        dialogHandlers={dialogHandlers}
        loading={loading}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}